import { useEffect, useState } from 'react';
import ArrowSteps from '../../../../../components/CommissioningProgress';
import DocumentUpload from './CriteriaTypes/DocumentUpload';
import { useParams } from 'react-router-dom';
import {
  getAssetById,
  updateAsset,
  getAssetCategoryById,
  getInterconnectionById,
  getInterconnectionCategoryById,
  updateInterconnection,
  getStageTasks,
  getIssues,
} from '../../../../../helpers/apiCalls';
import { useMsal } from '@azure/msal-react';
import useMaintenanceState from '../../../../../atoms/variables';
import { findById } from '../../../../../../helpers/misc';
import useTemplateState from '../../../../../../Template/atoms/variables';
import { Button, notification } from 'antd';
import { useTranslation } from 'react-i18next';

import './style.css';
import SystemDependency from './CriteriaTypes/SystemDependency';
import TaskReports from './CriteriaTypes/TaskReports';
import WebFormOpenner from './CriteriaTypes/WebFormOpenner';
import IssueTable from './CriteriaTypes/WebFormOpenner/IssueTable';
import IssueDetailsModal from './CriteriaTypes/WebFormOpenner/IssueDetailsModal';

const assetTypes = ['machine', 'assembly', 'equipment', 'component'];

const CommissioningTab = ({
  steps = [{ label: 'L1' }, { label: 'L2' }, { label: 'L3' }, { label: 'L4' }, { label: 'L5' }],
}) => {
  const { plantId, assetId, interconnectionId } = useParams();

  const { t } = useTranslation();
  const { instance } = useMsal();
  const { existingAssetTrees, viewDetailsItem } = useMaintenanceState();
  const [notificationApi, contextHolder] = notification.useNotification();

  const { setPendingApiCalls } = useTemplateState();

  const [currentStep, setCurrentStep] = useState(null);

  const [asset, setAsset] = useState(null);
  const [category, setCategory] = useState(null);

  const [stepProgress, setStepProgress] = useState({
    L1: 0,
    L2: 0,
    L3: 0,
    L4: 0,
    L5: 0,
  });
  const [mandatoryStepsCompletion, setMandatoryCompletion] = useState({
    L1: false,
    L2: false,
    L3: false,
    L4: false,
    L5: false,
  });
  const [completedSteps, setCompletedSteps] = useState({
    L1: false,
    L2: false,
    L3: false,
    L4: false,
    L5: false,
  });

  const [allTasks, setAllTasks] = useState([]);
  const [allIssues, setAllIssues] = useState([]);
  const [issueResponse, setIssueResponse] = useState([]);
  const [forceReload, setForceReload] = useState(false);
  const [refetchIssues, setRefetchIssues] = useState(false);

  const [forms, setForms] = useState([]);

  useEffect(() => {
    if (viewDetailsItem) triggerApiCalls();
  }, [viewDetailsItem]);

  useEffect(() => {
    if (asset?.completedSteps) {
      let curr = ['success', 'approved'].includes(asset?.status?.toLowerCase())
        ? Object.values(asset.completedSteps).lastIndexOf(true)
        : Object.values(asset.completedSteps).indexOf(false);
      if (curr === -1) curr = steps.length - 1;
      setCurrentStep(curr);
    }
  }, [asset]);

  useEffect(() => {
    if (currentStep !== null) {
      if (
        ['success', 'approved'].includes(asset?.status?.toLowerCase()) &&
        currentStep > getLastCompletedStep(asset)
      ) {
        console.log('Will update subject status to PENDING');
        if (assetId) updateAsset(instance, assetId, { status: 'Pending' });
        else updateInterconnection(instance, interconnectionId, { status: 'Pending' });
      } else {
        console.log('Status is ok');
      }
    }
  }, [currentStep]);

  const getLastCompletedStep = (subject) => {
    const steps = subject.completedSteps;
    const stepsArray = Object.keys(steps);

    // Encontra o último passo completo
    const currentStep = stepsArray.findLastIndex((step) => steps[step] === true);

    // Se todos os passos estiverem completos, retorna o último
    return currentStep !== -1 ? currentStep : stepsArray.length - 1;
  };

  useEffect(() => {
    if (currentStep === null) return;
    const phaseTitle = category?.commissioning?.phases[currentStep]?.name;

    const taskIds = category?.commissioning?.phases[currentStep]?.stages?.map((stage, index) => ({
      id: stage._id,
      indexId: `${stage._id}-${index}`,
    }));
    const approvedTasks = allTasks?.filter(
      (task) => task?.report?.status?.toLowerCase() === 'approved',
    );

    const completedTaskIds = taskIds?.filter((taskId) => {
      const result = approvedTasks.some((task) => {
        return task?.test?._id === taskId?.indexId || task?.test?._id === taskId?.id;
      });
      return result;
    });

    const completedIssueIds = allIssues?.filter(
      (issue) => issue?.report?.status?.toLowerCase() === 'approved',
    );

    setMandatoryCompletion((prev) => {
      return {
        ...prev,
        [phaseTitle]:
          completedTaskIds?.length === taskIds?.length &&
          completedIssueIds?.length === allIssues?.length,
      };
    });
    setStepProgress((prev) => {
      const totalItems = (taskIds?.length || 0) + (allIssues?.length || 0);
      const completedItems = (completedTaskIds?.length || 0) + (completedIssueIds?.length || 0);

      return {
        ...prev,
        [phaseTitle]: totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0,
      };
    });
  }, [allTasks, allIssues]);

  useEffect(() => {
    if (currentStep === null) return;
    if (category) fetchIssues(currentStep);
  }, [category, currentStep]);

  useEffect(() => {
    if (refetchIssues) {
      console.log('refetching issues');
      fetchIssues(currentStep);
      setRefetchIssues(false);
    }
  }, [refetchIssues]);

  const fetchIssues = async (currentStep) => {
    setPendingApiCalls((current) => current.concat(['getIssues']));
    try {
      console.log('original', {
        'subject._id': assetId ?? interconnectionId,
        stage: category?.commissioning?.phases[currentStep]?.name,
        'subject._type': assetId ? 'Asset' : 'Interconnection',
        uniqueValues: true,
        limit: 10,
      });

      const issues = await getIssues(instance, {
        'subject._id': assetId ?? interconnectionId,
        stage: category?.commissioning?.phases[currentStep]?.name,
        'subject._type': assetId ? 'Asset' : 'Interconnection',
        uniqueValues: true,
        limit: 10,
      });
      setIssueResponse(issues);

      setAllIssues(
        issues?.items?.map((issue) => ({
          test: { _id: issue?._id },
          task: { _id: issue?.task?._id },
          report: { status: issue?.status },
        })),
      );
    } catch (error) {
      console.log('Error fetching issues:', error);
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'getIssues'));
    }
  };

  useEffect(() => {
    if (category?.commissioning?.phases?.length > 0) {
      const collectedForms = category.commissioning.phases.flatMap((phase) =>
        phase.stages
          .filter(
            (stage) =>
              stage?.['en']?.key.includes('custom') && stage?.['en']?.platform !== 'mobile',
          )
          .map((stage) => ({ stage, phase: phase?.name })),
      );
      setForms(collectedForms);
    }
  }, [category]);

  const triggerApiCalls = async () => {
    try {
      setPendingApiCalls((current) => current.concat(['getAssetById']));

      const responses = await Promise.all([
        assetId
          ? getAssetById(instance, assetId)
          : getInterconnectionById(instance, interconnectionId),
        assetId
          ? getAssetCategoryById(
              instance,
              viewDetailsItem?.category?._id ??
                viewDetailsItem?.properties.find((p) => p.key === 'category')?.value?.id,
            )
          : getInterconnectionCategoryById(
              instance,
              viewDetailsItem?.category?._id ??
                viewDetailsItem?.properties.find((p) => p.key === 'category')?.value?.id,
            ),
      ]);

      const asset = responses[0];
      setAsset(asset);
      setCompletedSteps(asset.completedSteps);
      setCategory(responses[1]);
    } catch (err) {
      notificationApi.error({
        message: t('database_communication_failure'),
        description: err?.response?.data?.message
          ? `${err?.response?.data?.message} (${err?.response?.status})`
          : err?.message,
        duration: 5,
      });
      console.log(err);
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'getAssetById'));
    }
  };

  const handleNextStep = async () => {
    const currentLabel = steps[currentStep].label;
    if (mandatoryStepsCompletion[currentLabel]) {
      const updatedCompletedSteps = {
        ...completedSteps,
        [currentLabel]: true,
      };
      setCompletedSteps(updatedCompletedSteps);
      // acho que o back deve ser responsável por determinar quando mudar o completedSteps
      try {
        await saveCompletedSteps(updatedCompletedSteps);
      } catch (err) {
        console.error('Failed to save locked steps:', err);
      }
      setAllTasks([]);
      setCurrentStep((prev) => Math.min(prev + 1, 5));
    } else {
      console.warn(`Cannot proceed. Step ${currentLabel} is not completed.`);
    }
  };

  const saveCompletedSteps = async (updatedCompleted) => {
    setPendingApiCalls((current) => current.concat(['updateAsset']));

    try {
      const payload = {
        ...asset,
        completedSteps: updatedCompleted,
      };
      if (assetId) updateAsset(instance, assetId, payload);
      else updateInterconnection(instance, interconnectionId, payload);
    } catch (err) {
      console.error('Failed to update locked steps:', err);
    }
    setPendingApiCalls((current) => current.filter((item) => item !== 'updateAsset'));
  };

  const findItem = (id) => {
    if (existingAssetTrees) {
      let tree = existingAssetTrees.find((tree) => tree?.id === plantId);
      if (tree) {
        return findById(tree, id);
      }
    }
  };

  const handlePhaseStages = (stage, stageIndex, phaseTitle) => {
    if (stage?.['en']?.platform === 'mobile') {
      return (
        <>
          <TaskReports
            stage={stage}
            currentStepLabel={steps[currentStep]?.label ?? ''}
            setAllTasks={setAllTasks}
            issues={issueResponse}
            stageIndex={stageIndex}
            testId={stage._id}
          />
        </>
      );
    } else {
      switch (stage?.['en']?.key) {
        case 'file-upload':
          return (
            <DocumentUpload
              issues={issueResponse}
              asset={asset}
              phaseTitle={phaseTitle}
              stage={stage}
              stageIndex={stageIndex}
              type={
                interconnectionId
                  ? 'interconnection'
                  : assetTypes.includes(findItem(assetId)?.type)
                    ? 'asset'
                    : 'position'
              }
              setStepProgress={setStepProgress}
              isLocked={completedSteps[phaseTitle]}
              testId={stage._id}
              setAllTasks={setAllTasks}
              setRefetchIssues={setRefetchIssues}
            />
          );
        case 'system-dependency':
          return (
            <SystemDependency
              asset={asset}
              phaseTitle={phaseTitle}
              setStepProgress={setStepProgress}
              testId={stage._id}
              setAllTasks={setAllTasks}
              issues={issueResponse}
              stageIndex={stageIndex}
            />
          );
        default:
          // Returning nothing as forms will be rendered from the 'forms' state
          return;
      }
    }
  };

  const {
    setModalContent,
    setModalTitle,
    setModalStyle,
    setModalConfirm,
    setCleanOnCancel,
    setModalOpen,
  } = useTemplateState();

  const createIssue = (currentStep) => {
    setModalContent(
      <IssueDetailsModal
        stage={category?.commissioning?.phases[currentStep]?.name}
        givenIssue={null}
        handleInsertComment={() => {}}
        setForceReload={() => {
          if (allIssues.length > 0) {
            setForceReload(true);
            setAllIssues((prev) => [
              ...prev,
              {
                test: { _id: 'tmp' },
                task: { _id: 'tmp' },
                report: { status: 'Pending' },
              },
            ]);
          } else {
            fetchIssues(currentStep);
          }
        }}
      />,
    );
    setModalTitle(t('issue'));
    setModalStyle({
      padding: '2rem',
      gap: '1rem',
      maxHeight: '100%',
    });
    setModalConfirm(null);
    setCleanOnCancel(true);
    setModalOpen(true);
  };

  return (
    <div className='arrowStepsContainer'>

      {contextHolder}
      <ArrowSteps
        currentStep={currentStep}
        mandatoryStepsCompletion={mandatoryStepsCompletion}
        stepProgress={stepProgress}
        components={category?.commissioning?.phases?.map((phase) => {
          return (
            <div className='criteriaContainer'>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '28px',
                      fontWeight: '600',
                      marginBottom: '10px',
                    }}
                  >
                    {t('issues')}
                  </div>
                  <Button
                    size='large'
                    style={{
                      width: 'fit-content',
                      color: 'white',
                      border: '#1777FF',
                      backgroundColor: '#1777FF',
                    }}
                    onClick={() => createIssue(currentStep)}
                  >
                    {t('create_issue')}
                  </Button>
                </div>
                {issueResponse?.items?.length > 0 && (
                  <IssueTable
                    issues={issueResponse}
                    phaseTitle={phase?.name}
                    forceReload={forceReload}
                    setForceReload={setForceReload}
                  />
                )}
              </div>
              {forms.filter((form) => form.phase === phase.name).length > 0 && (
                <WebFormOpenner
                  data={forms}
                  setAllTasks={setAllTasks}
                  issues={issueResponse}
                  phaseTitle={phase?.name}
                />
              )}
              {phase.stages.map((stage, index) => handlePhaseStages(stage, index, phase?.name))}
              {currentStep !== steps.length - 1 && (
                <div style={{ textAlign: 'right' }}>
                  <Button
                    type='primary'
                    disabled={!mandatoryStepsCompletion[phase?.name]}
                    onClick={handleNextStep}
                    size='large'
                    style={{
                      borderColor: '#1777FF',
                      background: '#1777FF',
                      color: 'white',
                      opacity: mandatoryStepsCompletion[phase?.name] ? 1 : 0.5,
                    }}
                  >
                    {t('next')}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        steps={steps}
        onStepChange={(step) => {
          setCurrentStep(step);
          setAllTasks([]);
        }}
      />
    </div>
  );
};

export default CommissioningTab;
